import { useState, useEffect, useCallback } from 'react';
import { ChefHat, Clock, CheckCircle, Play, RefreshCcw, UtensilsCrossed, Package, CheckSquare, Square, PlayCircle, User, ChevronLeft, ChevronRight, Calendar, ClipboardList, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { format, addDays, subDays, isToday, isFuture, isPast } from 'date-fns';
import OrderPackingChecklist from '@/components/admin/OrderPackingChecklist';

import { API_URL } from '@/config/api';

export default function AdminKitchenPrep() {
  const { token, user } = useAuth();
  const [prepData, setPrepData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState('prep');
  
  // Packing state
  const [packingOrders, setPackingOrders] = useState([]);
  const [loadingPacking, setLoadingPacking] = useState(false);
  const [selectedPackingOrder, setSelectedPackingOrder] = useState(null);

  const fetchPrepData = useCallback(async (showLoading = false) => {
    if (showLoading || initialLoad) setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const endpoint = isToday(selectedDate) 
        ? `${API_URL}/api/kitchen/prep/today`
        : `${API_URL}/api/kitchen/prep/${dateStr}`;
      
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch prep data');
      
      const data = await response.json();
      setPrepData(data);
      if (showLoading || initialLoad) setSelectedItems(new Set()); // Clear selections only on explicit date change
    } catch (error) {
      console.error('Error fetching prep data:', error);
      toast.error('Failed to load kitchen prep data');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [token, selectedDate, initialLoad]);

  // Fetch packing orders
  const fetchPackingOrders = useCallback(async () => {
    setLoadingPacking(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await fetch(`${API_URL}/api/kitchen/orders-for-packing?date=${dateStr}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch packing orders');
      
      const data = await response.json();
      setPackingOrders(data.orders || []);
    } catch (error) {
      console.error('Error fetching packing orders:', error);
      // Don't show error toast as this is a secondary feature
    } finally {
      setLoadingPacking(false);
    }
  }, [token, selectedDate]);

  useEffect(() => {
    fetchPrepData(true); // Show loading on date change
    fetchPackingOrders();
  }, [selectedDate]);
  
  useEffect(() => {
    // Refresh every 30 seconds for real-time updates (no loading spinner)
    const interval = setInterval(() => fetchPrepData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchPrepData]);

  // Date navigation functions
  const goToPreviousDay = () => setSelectedDate(prev => subDays(prev, 1));
  const goToNextDay = () => setSelectedDate(prev => addDays(prev, 1));
  const goToToday = () => setSelectedDate(new Date());

  const updatePrepStatus = async (mealId, newStatus) => {
    if (!prepData) return;
    
    setUpdating(mealId);
    try {
      const response = await fetch(`${API_URL}/api/kitchen/prep/${prepData.date}/item/${mealId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ prep_status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      toast.success(`Prep status updated to ${newStatus}`);
      fetchPrepData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(null);
    }
  };

  // Bulk update function
  const bulkUpdateStatus = async (newStatus) => {
    if (!prepData || selectedItems.size === 0) return;
    
    setBulkUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/kitchen/prep/${prepData.date}/bulk-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ 
          meal_ids: Array.from(selectedItems),
          prep_status: newStatus 
        })
      });

      if (!response.ok) throw new Error('Failed to bulk update');

      const result = await response.json();
      toast.success(result.message);
      setSelectedItems(new Set());
      fetchPrepData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkUpdating(false);
    }
  };

  // Select/deselect item
  const toggleItemSelection = (mealId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(mealId)) {
        newSet.delete(mealId);
      } else {
        newSet.add(mealId);
      }
      return newSet;
    });
  };

  // Select all items
  const selectAllItems = () => {
    if (!prepData?.items) return;
    setSelectedItems(new Set(prepData.items.map(item => item.meal_id)));
  };

  // Deselect all items
  const deselectAllItems = () => {
    setSelectedItems(new Set());
  };

  // Check if all items are selected
  const allSelected = prepData?.items?.length > 0 && selectedItems.size === prepData.items.length;

  // Get items by status for bulk actions
  const pendingItems = prepData?.items?.filter(i => i.prep_status === 'pending') || [];
  const startedItems = prepData?.items?.filter(i => i.prep_status === 'started') || [];
  const selectedPendingCount = [...selectedItems].filter(id => 
    pendingItems.some(item => item.meal_id === id)
  ).length;
  const selectedStartedCount = [...selectedItems].filter(id => 
    startedItems.some(item => item.meal_id === id)
  ).length;

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'started': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'started': return <Play className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  // Calculate progress
  const completedCount = prepData?.items?.filter(i => i.prep_status === 'completed').length || 0;
  const totalCount = prepData?.items?.length || 0;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="kitchen-prep-loading">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-kitchen-prep">
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-foreground flex items-center gap-3">
            <ChefHat className="h-8 w-8 text-amber-500" />
            Kitchen Prep & Packing
          </h1>
          <p className="text-muted-foreground mt-1">
            {prepData?.total_orders || 0} orders • {packingOrders.length} awaiting packing
          </p>
        </div>
        
        {/* Date Navigation - Enhanced Visibility */}
        <div className="flex items-center gap-3">
          <Button 
            onClick={goToPreviousDay} 
            variant="outline" 
            size="icon"
            className="h-12 w-12 border-2"
            data-testid="prev-date-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <div className={`flex flex-col items-center px-6 py-3 rounded-xl min-w-[200px] border-2 ${
            isToday(selectedDate) 
              ? 'bg-primary/10 border-primary text-primary' 
              : isFuture(selectedDate) 
              ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-950/30 dark:border-amber-600' 
              : 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-950/30 dark:border-blue-600'
          }`}>
            <span className={`text-xs font-bold uppercase tracking-wider ${
              isToday(selectedDate) ? 'text-primary' : ''
            }`}>
              {isToday(selectedDate) ? '📅 TODAY' : isFuture(selectedDate) ? '⏳ UPCOMING' : '📁 PAST'}
            </span>
            <span className="font-bold text-lg flex items-center gap-2 mt-1">
              <Calendar className="h-5 w-5" />
              {format(selectedDate, 'EEE, MMM d')}
            </span>
            <span className="text-sm opacity-75">{format(selectedDate, 'yyyy')}</span>
          </div>
          
          <Button 
            onClick={goToNextDay} 
            variant="outline" 
            size="icon"
            className="h-12 w-12 border-2"
            data-testid="next-date-btn"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
          
          {!isToday(selectedDate) && (
            <Button 
              onClick={goToToday} 
              variant="default" 
              size="sm"
              className="ml-2 h-10 px-4 font-semibold"
              data-testid="today-btn"
            >
              Go to Today
            </Button>
          )}
          
          <Button onClick={fetchPrepData} variant="outline" size="icon" className="h-10 w-10" data-testid="refresh-prep-btn">
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Date Status Banner */}
      {isPast(selectedDate) && !isToday(selectedDate) && (
        <Card className="mb-4 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
          <CardContent className="p-3 flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
            <Calendar className="h-4 w-4" />
            Viewing historical prep data for {format(selectedDate, 'MMMM d, yyyy')}
          </CardContent>
        </Card>
      )}
      
      {isFuture(selectedDate) && (
        <Card className="mb-4 border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardContent className="p-3 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
            <Calendar className="h-4 w-4" />
            Viewing scheduled orders for {format(selectedDate, 'MMMM d, yyyy')}
          </CardContent>
        </Card>
      )}

      {/* Tabs for Prep vs Packing */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="prep" className="flex items-center gap-2">
            <ChefHat className="h-4 w-4" />
            Prep List ({totalCount})
          </TabsTrigger>
          <TabsTrigger value="packing" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Packing ({packingOrders.length})
          </TabsTrigger>
        </TabsList>

        {/* PREP TAB */}
        <TabsContent value="prep" className="mt-6">
          {/* Progress Overview */}
          <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg text-foreground">Daily Progress</h3>
              <p className="text-sm text-muted-foreground">
                {completedCount} of {totalCount} items completed
              </p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                {Math.round(progressPercent)}%
              </span>
            </div>
          </div>
          <Progress value={progressPercent} className="h-3" />
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {prepData?.items?.length > 0 && (
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Selection Controls */}
              <div className="flex items-center gap-3 border-r border-border pr-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={allSelected ? deselectAllItems : selectAllItems}
                  className="gap-2"
                  data-testid="select-all-btn"
                >
                  {allSelected ? (
                    <>
                      <CheckSquare className="h-4 w-4" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4" />
                      Select All
                    </>
                  )}
                </Button>
                {selectedItems.size > 0 && (
                  <Badge variant="secondary" className="font-medium">
                    {selectedItems.size} selected
                  </Badge>
                )}
              </div>

              {/* Bulk Action Buttons */}
              {selectedItems.size > 0 && (
                <div className="flex items-center gap-2">
                  {/* Start All Selected */}
                  {selectedPendingCount > 0 && (
                    <Button
                      onClick={() => bulkUpdateStatus('started')}
                      disabled={bulkUpdating}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 gap-2"
                      data-testid="bulk-start-btn"
                    >
                      <PlayCircle className="h-4 w-4" />
                      Start All ({selectedPendingCount})
                    </Button>
                  )}

                  {/* Complete All Selected */}
                  {selectedStartedCount > 0 && (
                    <Button
                      onClick={() => bulkUpdateStatus('completed')}
                      disabled={bulkUpdating}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 gap-2"
                      data-testid="bulk-complete-btn"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Complete All ({selectedStartedCount})
                    </Button>
                  )}

                  {/* Start All (regardless of current status) */}
                  <Button
                    onClick={() => bulkUpdateStatus('started')}
                    disabled={bulkUpdating}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <Play className="h-4 w-4" />
                    Set All to Started
                  </Button>

                  {/* Complete All (regardless of current status) */}
                  <Button
                    onClick={() => bulkUpdateStatus('completed')}
                    disabled={bulkUpdating}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Set All to Completed
                  </Button>
                </div>
              )}

              {/* Current User Info */}
              <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                <span>Logged in as: {user?.name || user?.email}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prep Items */}
      {!prepData?.items || prepData.items.length === 0 ? (
        <Card className="bg-muted/50 border-dashed">
          <CardContent className="p-12 text-center">
            <UtensilsCrossed className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">No Orders for Today</h3>
            <p className="text-muted-foreground/80">There are no paid orders requiring preparation today.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {prepData.items.map((item) => (
            <Card 
              key={item.meal_id} 
              className={`transition-all duration-200 ${item.prep_status === 'completed' ? 'opacity-70' : ''} ${selectedItems.has(item.meal_id) ? 'ring-2 ring-primary' : ''}`}
              data-testid={`prep-item-${item.meal_id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <Checkbox
                    checked={selectedItems.has(item.meal_id)}
                    onCheckedChange={() => toggleItemSelection(item.meal_id)}
                    className="mt-1"
                    data-testid={`select-item-${item.meal_id}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {item.name}
                      </CardTitle>
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.name} 
                          className="w-14 h-14 rounded-lg object-cover ml-2"
                        />
                      )}
                    </div>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-muted-foreground" />
                    <span className="text-2xl font-bold text-foreground">
                      {item.quantity_ordered}
                    </span>
                    <span className="text-muted-foreground text-sm">portions</span>
                  </div>
                  <Badge className={`${getStatusColor(item.prep_status)} flex items-center gap-1.5`}>
                    {getStatusIcon(item.prep_status)}
                    {item.prep_status?.charAt(0).toUpperCase() + item.prep_status?.slice(1)}
                  </Badge>
                </div>

                {/* Show who updated this item */}
                {item.updated_by && (
                  <div className="mb-3 p-2 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <User className="h-3 w-3" />
                      <span className="font-medium">{item.updated_by.name}</span>
                      <span>•</span>
                      <span>{item.updated_by.timestamp ? format(new Date(item.updated_by.timestamp), 'MMM d, h:mm a') : ''}</span>
                    </div>
                  </div>
                )}

                {/* Status Action Buttons */}
                <div className="flex gap-2">
                  {item.prep_status === 'pending' && (
                    <Button 
                      onClick={() => updatePrepStatus(item.meal_id, 'started')}
                      disabled={updating === item.meal_id}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      data-testid={`start-prep-${item.meal_id}`}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start Prep
                    </Button>
                  )}
                  {item.prep_status === 'started' && (
                    <>
                      <Button 
                        onClick={() => updatePrepStatus(item.meal_id, 'pending')}
                        disabled={updating === item.meal_id}
                        variant="outline"
                        className="flex-1"
                      >
                        Reset
                      </Button>
                      <Button 
                        onClick={() => updatePrepStatus(item.meal_id, 'completed')}
                        disabled={updating === item.meal_id}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid={`complete-prep-${item.meal_id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Complete
                      </Button>
                    </>
                  )}
                  {item.prep_status === 'completed' && (
                    <Button 
                      onClick={() => updatePrepStatus(item.meal_id, 'started')}
                      disabled={updating === item.meal_id}
                      variant="outline"
                      className="flex-1"
                    >
                      <RefreshCcw className="h-4 w-4 mr-2" />
                      Reopen
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        {/* PACKING TAB */}
        <TabsContent value="packing" className="mt-6">
          {/* Packing Instructions */}
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <div>
                  <h3 className="font-semibold text-foreground">Order Packing Checklist</h3>
                  <p className="text-sm text-muted-foreground">
                    Verify all items before marking orders as ready for collection
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Packing Orders List */}
          {loadingPacking ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : packingOrders.length === 0 ? (
            <Card className="bg-muted/50 border-dashed">
              <CardContent className="p-12 text-center">
                <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">No Orders to Pack</h3>
                <p className="text-muted-foreground/80">
                  All orders for {format(selectedDate, 'MMMM d')} have been packed or are not yet paid.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {packingOrders.map((order) => (
                <Card 
                  key={order.order_id} 
                  className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedPackingOrder(order)}
                  data-testid={`packing-order-${order.order_id}`}
                >
                  <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <CardTitle className="text-base font-semibold text-foreground">
                          {order.customer_name || 'Customer'}
                        </CardTitle>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        #{order.order_id?.slice(-8)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    {/* Items — show ALL items with sides */}
                    <div className="space-y-2 mb-4">
                      {order.items?.map((item, idx) => (
                        <div key={item.item_id || `item-${idx}`} className="text-sm border-l-2 border-primary/30 pl-2">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                              {item.quantity}
                            </span>
                            <span className="font-medium text-foreground">{item.name}</span>
                          </div>
                          {item.selected_sides?.length > 0 && (
                            <div className="ml-7 mt-0.5 space-y-0.5">
                              {item.selected_sides.map((side, sIdx) => (
                                <div key={side.side_id || `side-${sIdx}`} className="flex items-center gap-1.5 text-xs">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                                  <span className="text-green-700 dark:text-green-400 font-medium">{side.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Delivery info */}
                    {order.delivery_info?.delivery_type && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Truck className="h-4 w-4" />
                        <span className="capitalize">{order.delivery_info.delivery_type}</span>
                      </div>
                    )}

                    {/* Action button */}
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={(e) => { e.stopPropagation(); setSelectedPackingOrder(order); }}
                    >
                      <ClipboardList className="h-4 w-4 mr-2" />
                      Start Packing Checklist
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Packing Checklist Modal */}
      <OrderPackingChecklist
        isOpen={!!selectedPackingOrder}
        onClose={() => setSelectedPackingOrder(null)}
        order={selectedPackingOrder}
        token={token}
        onComplete={() => {
          fetchPackingOrders();
          fetchPrepData(false);
        }}
      />
    </div>
  );
}
